import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { anthropometricService } from '../services';
import { ChevronDown, ChevronUp, Save, X } from 'lucide-react';
import Swal from 'sweetalert2';

const AnthropometricForm = () => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState({
    basic: true,
    perimeters: false,
    diameters: false,
    skinfolds: false,
  });
  
  const [formData, setFormData] = useState({
    measurement_date: new Date().toISOString().split('T')[0],
    weight: '',
    height: '',
    sitting_height: '',
    arm_span: '',
    perimeter_wrist: '',
    perimeter_arm_relaxed: '',
    perimeter_arm_flexed: '',
    perimeter_waist_min: '',
    perimeter_waist_umbilical: '',
    perimeter_hip_max: '',
    perimeter_thigh_mid: '',
    perimeter_calf_max: '',
    diameter_wrist: '',
    diameter_humerus: '',
    diameter_femur: '',
    skinfold_triceps: '',
    skinfold_subscapular: '',
    skinfold_biceps: '',
    skinfold_iliac_crest: '',
    skinfold_supraspinal: '',
    skinfold_abdominal: '',
    skinfold_front_thigh: '',
    skinfold_medial_calf: '',
    notes: '',
  });

  const [gender, setGender] = useState('male');

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Filtrar campos vacíos
      const dataToSend = Object.fromEntries(
        Object.entries(formData).filter(([key, value]) => value !== '')
      );
      
      // Agregar género para cálculos
      dataToSend.gender = gender;
      
      await anthropometricService.create(dataToSend);
      
      await Swal.fire({
        title: '¡Éxito!',
        text: 'Medición registrada correctamente',
        icon: 'success',
        confirmButtonColor: '#8b5cf6',
      });
      
      navigate('/anthropometric');
    } catch (error) {
      console.error('Error saving measurement:', error);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo guardar la medición',
        icon: 'error',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  const SectionHeader = ({ title, section, icon }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-100 to-indigo-100 hover:from-purple-200 hover:to-indigo-200 rounded-xl transition-all mb-4"
    >
      <div className="flex items-center gap-3">
        <span className="text-3xl">{icon}</span>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>
      {openSections[section] ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
    </button>
  );

  const InputField = ({ label, name, unit, min, max, step = "0.1", helpText }) => (
    <div className="mb-4">
      <label className="block text-sm font-bold text-gray-700 mb-2">
        {label} {unit && <span className="text-purple-600">({unit})</span>}
      </label>
      <input
        type="number"
        name={name}
        value={formData[name]}
        onChange={handleChange}
        step={step}
        min={min}
        max={max}
        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium"
        placeholder={`Ej: ${min || '0'}`}
      />
      {helpText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="animate-fadeIn max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-extrabold mb-2 text-gray-900">
            📏 Nueva Medición
          </h1>
          <p className="text-gray-700 text-lg font-semibold">
            Completa los campos que puedas medir. Los cálculos se realizarán automáticamente.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Medidas Básicas */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <SectionHeader title="Medidas Básicas y Configuración" section="basic" icon="📊" />
            
            {openSections.basic && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Fecha de Medición *
                  </label>
                  <input
                    type="date"
                    name="measurement_date"
                    value={formData.measurement_date}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Sexo (para cálculos) *
                  </label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium"
                  >
                    <option value="male">👨 Masculino</option>
                    <option value="female">👩 Femenino</option>
                  </select>
                </div>

                <InputField
                  label="Peso"
                  name="weight"
                  unit="kg"
                  min="20"
                  max="300"
                  helpText="Tu peso corporal actual"
                />

                <InputField
                  label="Talla (Altura)"
                  name="height"
                  unit="cm"
                  min="100"
                  max="250"
                  helpText="Tu altura en centímetros"
                />

                <InputField
                  label="Talla Sentado"
                  name="sitting_height"
                  unit="cm"
                  min="50"
                  max="150"
                  helpText="Altura desde el asiento hasta la cabeza"
                />

                <InputField
                  label="Envergadura"
                  name="arm_span"
                  unit="cm"
                  min="100"
                  max="300"
                  helpText="Distancia de punta a punta de los dedos con brazos extendidos"
                />
              </div>
            )}
          </div>

          {/* Perímetros */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <SectionHeader title="Perímetros (Circunferencias)" section="perimeters" icon="⭕" />
            
            {openSections.perimeters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Muñeca"
                  name="perimeter_wrist"
                  unit="cm"
                  min="10"
                  max="50"
                />

                <InputField
                  label="Brazo Relajado"
                  name="perimeter_arm_relaxed"
                  unit="cm"
                  min="15"
                  max="70"
                />

                <InputField
                  label="Brazo en Contracción"
                  name="perimeter_arm_flexed"
                  unit="cm"
                  min="15"
                  max="70"
                  helpText="Con el bíceps contraído"
                />

                <InputField
                  label="Cintura Mínima"
                  name="perimeter_waist_min"
                  unit="cm"
                  min="40"
                  max="200"
                  helpText="Parte más estrecha del abdomen"
                />

                <InputField
                  label="Cintura a Nivel del Ombligo"
                  name="perimeter_waist_umbilical"
                  unit="cm"
                  min="40"
                  max="200"
                />

                <InputField
                  label="Cadera Máxima"
                  name="perimeter_hip_max"
                  unit="cm"
                  min="50"
                  max="200"
                  helpText="Parte más ancha de la cadera"
                />

                <InputField
                  label="Muslo Medio"
                  name="perimeter_thigh_mid"
                  unit="cm"
                  min="30"
                  max="100"
                />

                <InputField
                  label="Pierna Máximo (Pantorrilla)"
                  name="perimeter_calf_max"
                  unit="cm"
                  min="20"
                  max="70"
                />
              </div>
            )}
          </div>

          {/* Diámetros */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <SectionHeader title="Diámetros Óseos" section="diameters" icon="📐" />
            
            {openSections.diameters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Diámetro de Muñeca"
                  name="diameter_wrist"
                  unit="cm"
                  min="3"
                  max="15"
                  helpText="Ancho del hueso de la muñeca"
                />

                <InputField
                  label="Diámetro del Húmero"
                  name="diameter_humerus"
                  unit="cm"
                  min="4"
                  max="15"
                  helpText="Ancho del codo"
                />

                <InputField
                  label="Diámetro del Fémur"
                  name="diameter_femur"
                  unit="cm"
                  min="5"
                  max="20"
                  helpText="Ancho de la rodilla"
                />
              </div>
            )}
          </div>

          {/* Pliegues Cutáneos */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <SectionHeader title="Pliegues Cutáneos (Adipómetro)" section="skinfolds" icon="📏" />
            
            {openSections.skinfolds && (
              <>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-blue-900 font-medium">
                    💡 <strong>Importante:</strong> Los pliegues se miden en milímetros (mm) usando un adipómetro o plicómetro. 
                    Completa al menos 3 pliegues (tríceps, abdomen y muslo) para obtener el cálculo de porcentaje de grasa corporal.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Tríceps"
                    name="skinfold_triceps"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Parte posterior del brazo"
                  />

                  <InputField
                    label="Subescapular"
                    name="skinfold_subscapular"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Debajo del omóplato"
                  />

                  <InputField
                    label="Bíceps"
                    name="skinfold_biceps"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Parte frontal del brazo"
                  />

                  <InputField
                    label="Cresta Ilíaca"
                    name="skinfold_iliac_crest"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Encima del hueso de la cadera"
                  />

                  <InputField
                    label="Supraespinal"
                    name="skinfold_supraspinal"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Arriba y adelante de la cresta ilíaca"
                  />

                  <InputField
                    label="Abdominal"
                    name="skinfold_abdominal"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Al lado del ombligo"
                  />

                  <InputField
                    label="Muslo Anterior"
                    name="skinfold_front_thigh"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Parte frontal del muslo"
                  />

                  <InputField
                    label="Pierna Medial (Pantorrilla)"
                    name="skinfold_medial_calf"
                    unit="mm"
                    min="2"
                    max="50"
                    helpText="Parte interna de la pantorrilla"
                  />
                </div>
              </>
            )}
          </div>

          {/* Notas */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              📝 Notas Adicionales (Opcional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium"
              placeholder="Ej: Medición post-vacaciones, cambio en la dieta, inicio de nuevo programa..."
            ></textarea>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/anthropometric')}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-900 font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <X size={20} />
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
              }}
              className="flex-1 text-white font-bold py-4 px-6 rounded-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <Save size={20} />
              Guardar Medición
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AnthropometricForm;
